import React from 'react'

import { Card, CardContent, CardMedia, Chip, makeAppStyles, RoundedPlainTextButton } from '@percept/mui'

import { MediaAcademyWebinar } from './typings'

import { DesktopWindows, OpenInNew, Schedule, Slideshow, Today, Videocam } from '@material-ui/icons'

import { capitalize } from 'lodash-es'

import { formatLocal } from '@percept/utils'

import fallbackImage from '../../images/news/fallback.jpg'


const imageHeight = 220


const useChipStyles = makeAppStyles( theme => ({
  root: {
    height: 20,
    padding: 2,
  },
  label: {
    fontSize: 12,
  },
}))


const useWebinarStyles = makeAppStyles( theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    borderRadius: 8,
  },
  cardMedia: {
    height: imageHeight,
    objectPosition: 'top',
  },
  imgPlaceholder: {
    height: imageHeight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imgPlaceholderIcon: {
    color: theme.palette.text.disabled,
    fontSize: 128,
  },
  cardContent: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  metadata: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 12,
    lineHeight: '26px',
    marginBottom: theme.spacing(2),
  },
  date: {
    marginRight: theme.spacing(1),
  },
  dateTime: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    fontSize: 13,
    fontWeight: theme.typography.fontWeightBold,
    '&:first-of-type': {
      marginRight: theme.spacing(1.5),
    },
  },
  dateTimeIcon: {
    color: theme.palette.text.disabled,
    fontSize: 18,
    marginRight: 3,
  },
  category: {
    marginLeft: 'auto',
  },
  metadataSeparator: {
    margin: theme.spacing(0, 0.5),
  },
  titleWrap: {
    marginTop: theme.spacing(2),
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 22,
    lineHeight: '28px',
    fontWeight: 700,
    [theme.breakpoints.down('lg')]: {
      fontSize: 20,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
    }
  },
  titleIcon: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: '100%',
    marginLeft: theme.spacing(1),
  },
  readTimeIcon: {
    paddingRight: theme.spacing(1),
  },
  summary: {
    flexGrow: 1,
    fontSize: 16,
    lineHeight: '22px',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      fontSize: 16,
      lineHeight: '20px',
    }
  },
  summaryLink: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 'auto',
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    fontSize: 18,
    fontWeight: 500,
    color: theme.palette.primary.main,
    [theme.breakpoints.down('sm')]: {
      fontSize: 16,
    }
  },
  webinarLinks: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(0, 1),
    lineHeight: '48px',
  },
  webinarLink: {
    '&:first-of-type': {
      marginRight: theme.spacing(3),
    },
  },
}))

export const Webinar = ({
  title,
  summary,
  start_datetime,
  category,
  webinar_url,
  additional_url,
  image_url,
}: MediaAcademyWebinar): JSX.Element => {
  const date = new Date(start_datetime)
  const classes = useWebinarStyles()
  const chipClasses = useChipStyles()
  return (
    <Card
      elevation={4}
      className={classes.card}>
      <CardMedia
        component='img'
        className={classes.cardMedia}
        src={image_url || fallbackImage} />
      {/* { image_url ? (
        <CardMedia
          component='img'
          className={classes.cardMedia}
          src={image_url} />
      ) : (
        <div className={classes.imgPlaceholder}>
          <Videocam className={classes.imgPlaceholderIcon} />
        </div>
      )} */}
      <CardContent className={classes.cardContent}>
        <div className={classes.metadata}>
          <div className={classes.dateTime}>
            <Today className={classes.dateTimeIcon} />
            { formatLocal(date, 'do MMM yyyy') }
          </div>

          <div className={classes.dateTime}>
            <Schedule className={classes.dateTimeIcon} />
            { formatLocal(date, 'HH:mm') }
          </div>

          <Chip
            className={classes.category}
            variant='outlined'
            size='small'
            classes={chipClasses}
            color='primary'
            label={capitalize(category)} />
        </div>

        <div className={classes.title}>
          { title }
        </div>

        <div className={classes.summary}>
          { summary }
        </div>

        { !!(webinar_url || additional_url) && (
          <div className={classes.webinarLinks}>
            { webinar_url && (
              <a
                className={classes.webinarLink}
                href={webinar_url}
                rel='noopener noreferrer'
                target='_blank'>
                <RoundedPlainTextButton
                  variant='contained'
                  color='primary'
                  size='small'
                  startIcon={<DesktopWindows />}
                  endIcon={<OpenInNew />}>
                  Webinar
                </RoundedPlainTextButton>
              </a>
            )}

            { additional_url && (
              <a
                className={classes.webinarLink}
                href={additional_url}
                rel='noopener noreferrer'
                target='_blank'>
                <RoundedPlainTextButton
                  variant='contained'
                  color='secondary'
                  size='small'
                  startIcon={<Slideshow />}
                  endIcon={<OpenInNew />}>
                  Presentation
                </RoundedPlainTextButton>
              </a>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
